import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

// #82 (Task 4): Herausgelöst aus dem bisherigen Datenart-Editor in catalog-table.tsx — dort war
// die Combobox ein eigener Zweig in EditorField (kombiniert mit "+ anlegen"/Tippgeähnlichkeit).
// catalog-table.tsx zieht seither auf diese Bausteine, AddMember (E2E-Mitglied hinzufügen, #82)
// nutzt dieselben. Die Filterlogik (was passt) und die "Ausgewählt"-Semantik bleiben bewusst beim
// Aufrufer — hier steht nur das gemeinsame, a11y-kritische Ein-/Ausgabegeruest.
//
// Traegt zwei teuer erarbeitete a11y-Details, die beim Herausloesen NICHT verloren gehen dürfen:
// - tabIndex={-1} auf ALLEN Dropdown-Eintraegen (ComboboxOption): Eine Optionsliste gehoert nicht
//   in die Tab-Reihenfolge (Combobox-Konvention — Tab verlaesst das Feld, die Liste waehlt man
//   per Maus oder ueber den Aufrufer verkabelte Pfeiltasten). Ohne das bestimmt der Browser beim
//   Tab einen dieser Knoepfe als Ziel, waehrend onBlur gleichzeitig das Dropdown schliesst und
//   das Ziel aus dem DOM reisst — der Fokus landet auf <body> und der naechste Tab beginnt von
//   vorn (gemeldeter Befund: Tab aus der Datenart sprang nicht ins Wert-Feld, sondern ins Leere).
// - onMouseDown mit preventDefault auf den Eintraegen: verhindert, dass der Input vor dem Klick
//   blurred wird — sonst schliesst onBlur das Dropdown, bevor der Klick auf die Option ueberhaupt
//   ankommt.

/**
 * Eingabefeld + Dropdown-Huelle. Oeffnet/schliesst sich selbst ueber Fokus/Blur des Inputs;
 * reicht `open` (und eine `close()`-Funktion fuer den Fall eines Klicks, der den Blur per
 * `preventDefault` unterdrueckt hat) an den Kindfunktions-Aufruf durch. Was IM Dropdown steht
 * (gefilterte Optionen, "+ anlegen", Kein-Treffer-Hinweis, Kappungs-Hinweis …) entscheidet
 * ausschliesslich der Aufrufer — Filterregeln unterscheiden sich je Einsatzort (Task-Brief:
 * matchesQuery fuer AddMember, Tippgeaehnlichkeit + Inline-Anlage fuer den Datenart-Editor).
 */
export type ComboboxHandle = { close: () => void };

// #112: Oeffnungsrichtung der Liste. Bewusst ueber Context statt ueber eine Prop — ComboboxList
// wird an fuenf Stellen gerendert (catalog-table, test-case-picker, users-step, bulk-action-bar
// und hier), die sonst alle nur zum Durchreichen angefasst werden muessten.
const DropUpContext = createContext(false);

/** Hoehe, die die Liste beanspruchen kann (`max-h-48` = 12rem), plus Abstand zum Fensterrand. */
const LIST_MAX_HEIGHT = 192;
const EDGE_GAP = 8;

// I7 (Review-Finding): der alte <select> brachte Combobox-Semantik und einen sichtbaren
// geschlossenen Zustand ("Testfall hinzufügen") von Haus aus mit — beides ging bei der Ablösung
// verloren. Billig nachzuruesten, weil jetzt alles an einer Stelle liegt: `placeholder` fuer den
// sichtbaren Leerzustand, plus die vier ARIA-Combobox-Attribute (role/aria-expanded/aria-controls/
// aria-activedescendant) fuer Screenreader. `listboxId`/`activeId` sind beide optional — ein
// Aufrufer ohne Pfeiltasten-Highlight (der Datenart-Editor in catalog-table.tsx) muss nichts extra
// vergeben, Combobox erzeugt sich dann selbst eine Id und reicht sie ueber `children` durch.
export const Combobox = forwardRef<
  ComboboxHandle,
  {
    label: string;
    value: string;
    onChange: (value: string) => void;
    /** Aufrufer-eigene Tastaturlogik (z. B. Pfeiltasten-Navigation in AddMember). Combobox selbst
     *  fasst keine Taste an — Enter/Escape im Datenart-Editor werden weiterhin vom umschliessenden
     *  EditRow (Zeilen-`onKeyDown`) verarbeitet, unveraendert wie vor der Extraktion. Ein externer
     *  Handler (per `ref`) kann das Dropdown nach einer per Tastatur getroffenen Auswahl schliessen
     *  — die Kindfunktions-`close()` ist nur INNERHALB des Dropdowns erreichbar (Mausklicks).*/
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    /** #207: zusaetzlich zum eigenen Oeffnen/Schliessen — SearchSelect markiert beim Fokus den
     *  Text und stellt beim Verlassen den gewaehlten Namen wieder her. */
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    autoFocus?: boolean;
    className: string;
    /** Sichtbarer Leerzustand, solange nichts getippt/ausgewaehlt ist — der ersetzte <select>
     *  zeigte geschlossen z. B. "Testfall hinzufügen", ein leeres Textfeld zeigt gar nichts. */
    placeholder?: string;
    /** Eigene Id fuer role=listbox/aria-controls — nur noetig, wenn der Aufrufer zusaetzlich
     *  `activeId` fuer aria-activedescendant setzen will (das muss VOR dem Kindfunktions-Aufruf
     *  feststehen). Ohne eigene Vergabe erzeugt Combobox selbst eine Id (useId). */
    listboxId?: string;
    /** #114 (A9): gesperrtes Eingabefeld — gebraucht in der Sammelaktions-Leiste am
     *  abgeschlossenen Testplan (bulk-action-bar.tsx). Nativ `disabled`: das Feld nimmt keinen
     *  Fokus mehr an, damit kann sich das Dropdown gar nicht erst oeffnen. */
    disabled?: boolean;
    /** DOM-Id des aktuell per Pfeiltaste hervorgehobenen Eintrags fuer aria-activedescendant —
     *  nur der Aufrufer kennt seinen "aktiven" Index (z. B. AddMemberPicker). */
    activeId?: string;
    children: (ctx: { open: boolean; close: () => void; listboxId: string }) => ReactNode;
  }
>(function Combobox(
  {
    label,
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    autoFocus,
    className,
    placeholder,
    disabled,
    listboxId: listboxIdProp,
    activeId,
    children,
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const generatedListboxId = useId();
  const listboxId = listboxIdProp ?? generatedListboxId;

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useImperativeHandle(ref, () => ({ close: () => setOpen(false) }), []);

  // #112 (Punkt 4): Die Liste oeffnete bisher ausnahmslos nach unten. In einer unten klebenden
  // Leiste — bulk-action-bar.tsx traegt `sticky bottom-0` — landet sie damit zwangslaeufig
  // ausserhalb des Sichtfelds: Der gemeldete Fall war die Tester-Massenzuweisung, bei der man
  // tippen, aber keinen Treffer anklicken konnte. Beim Oeffnen messen, ob unterhalb ueberhaupt
  // Platz ist, sonst nach oben klappen. Bewusst KEIN Portal (anders als bei #68/#107): Dort war
  // die Ursache ein Transform-Vorfahre, der `position: fixed` bricht — hier stimmt schlicht die
  // Richtung nicht, und die Liste bleibt im `relative`-Wrapper korrekt positioniert.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom - EDGE_GAP;
      const above = rect.top - EDGE_GAP;
      // Nur hochklappen, wenn unten zu wenig Platz ist UND oben mehr ist. Sonst bleibt unten die
      // bessere Wahl — in einem sehr flachen Fenster sind beide Seiten eng, dann gewinnt die
      // gewohnte Richtung.
      setDropUp(below < LIST_MAX_HEIGHT && above > below);
    };
    measure();
    window.addEventListener('resize', measure);
    // capture=true: auch Scrollen INNERER Container muss die Richtung neu bewerten — ein
    // Scroll-Ereignis dort steigt nicht bis window auf.
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          setOpen(true);
          onFocus?.(e);
        }}
        onBlur={() => {
          setOpen(false);
          onBlur?.();
        }}
        className={className}
      />
      <DropUpContext.Provider value={dropUp}>
        {children({ open, close: () => setOpen(false), listboxId })}
      </DropUpContext.Provider>
    </div>
  );
});

// Kurz gehalten, damit die JSX-Zeile unten inkl. `id`/`role` in EINER Zeile bleibt — nur so
// greifen die drei a11y-Lint-Unterdrueckungen (biome haengt sie an die Zeile des Tags, nicht an
// einzelne, ueber mehrere Zeilen verteilte Attribute).
const LIST_BASE =
  'absolute z-10 max-h-48 w-full overflow-auto rounded-btn border border-[var(--border)] bg-bg-surface text-sm shadow-md';
// `top-full` war vorher implizit (absolut positioniert ohne top/bottom = statische Position direkt
// unter dem Input) — jetzt ausgeschrieben, damit die Gegenrichtung ueberhaupt formulierbar ist.
const LIST_CLASS_DOWN = `${LIST_BASE} top-full mt-1`;
const LIST_CLASS_UP = `${LIST_BASE} bottom-full mb-1`;

/** Der Dropdown-Container: absolute Positionierung unter dem Input, feste Maximalhoehe + Scroll —
 *  1:1 aus dem bisherigen Datenart-Editor uebernommen. `id` (von Combobox durchgereicht) verbindet
 *  die Liste mit `aria-controls` auf dem Input (I7). */
export function ComboboxList({ id, children }: { id?: string; children: ReactNode }) {
  const dropUp = useContext(DropUpContext);
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: ARIA-Combobox-Pattern — die Liste selbst ist nicht fokussierbar, der Input traegt aria-activedescendant (s. ComboboxOption).
    // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <ul role="listbox"> ist die von der ARIA-Spec vorgesehene Kombination fuer eine Options-Liste.
    // biome-ignore lint/a11y/useSemanticElements: ein natives <select> kann die freie Kind-Formatierung/Kappungs-Hinweise (ComboboxNotice) nicht abbilden — genau der Grund fuer diese Combobox (#82).
    <ul id={id} role="listbox" className={dropUp ? LIST_CLASS_UP : LIST_CLASS_DOWN}>
      {children}
    </ul>
  );
}

/** Ein waehlbarer Eintrag der Dropdown-Liste — traegt die beiden a11y-Details aus dem Datei-Kopf.
 *  I7: `id` (fuer aria-activedescendant) und `selected` (fuer aria-selected) sind optional — nur
 *  Aufrufer mit eigener Pfeiltasten-Navigation (AddMemberPicker) vergeben sie. */
export function ComboboxOption({
  id,
  selected,
  onSelect,
  className,
  children,
}: {
  id?: string;
  selected?: boolean;
  onSelect: () => void;
  className?: string;
  children: ReactNode;
}) {
  // ARIA-Combobox-Pattern (WAI-ARIA APG "Combobox with List Autocomplete"): der Fokus bleibt
  // IMMER im Input, die Optionen sind absichtlich nicht eigenstaendig fokussierbar —
  // aria-activedescendant auf dem Input (Combobox oben) uebernimmt die "virtuelle"
  // Fokus-Kommunikation an Screenreader. Ein natives <option> existiert nur innerhalb von
  // <select>/<datalist> und koennte keine eigene onSelect-Logik/Formatierung tragen — genau der
  // Grund, warum diese Combobox ueberhaupt den <select> ersetzt (#82). Drei generische
  // a11y-Linter-Regeln greifen hier bewusst nicht.
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: siehe Kommentar oben — kein Tab-Ziel gewollt.
    // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <li role="option"> ist die von der ARIA-Spec vorgesehene Kombination.
    // biome-ignore lint/a11y/useSemanticElements: natives <option> kann keine onSelect-Logik tragen, s. o.
    <li id={id} role="option" aria-selected={selected}>
      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onSelect}
        className={
          className ?? 'block w-full px-2 py-1 text-left text-text-body hover:bg-brand-accent/10'
        }
      >
        {children}
      </button>
    </li>
  );
}

/** Ein reiner Hinweistext in der Liste (kein Button) — z. B. die #78-Kappungs- oder die
 *  Kein-Treffer-Meldung. Kein tabIndex/onMouseDown noetig, weil hier nichts ausgewaehlt wird. */
export function ComboboxNotice({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <li className={className ?? 'px-2 py-1.5 text-xs text-text-muted'}>{children}</li>;
}
